/**
 * `vendor_limits.value` is a JSONB object literally named `value` (v2-L-4, `admin.py`): the API never
 * flattens it. This module is the one place that turns `{key: "per_minute", value: {limit: 100}}` into
 * "100 requests / minute" for the Settings screen, so the panel never renders raw JSON in its default view.
 *
 * The seeded targets, read from the migrations that write them (`0002_xero.py`, `0007_phase3.py`,
 * `0009_p4_attachment_seeds.py`): `per_minute`, `per_hour`, `per_day`, `page_size`, `concurrency`,
 * `attachment_share_of_daily`, plus vendor-specific variants (`app_per_minute`, `batch_ops`, …) and
 * `payload_bytes`/`body_bytes`-shaped byte caps. Anything not on this list falls back to a visible,
 * un-prettified line rather than being silently dropped.
 */

export type VendorLimitValue = Record<string, unknown>;

/** `system` codes as seeded, mapped to the names an operator recognises. */
export const SYSTEM_LABELS: Record<string, string> = {
  xero: "Xero",
  qbo: "QuickBooks Online",
  freeagent: "FreeAgent",
  sage_bc: "Sage Business Cloud",
  sage50: "Sage 50",
  sage_50: "Sage 50",
  hmrc: "HMRC",
  lucibook: "LuciBook",
};

/** Falls back to a humanised version of the raw code (`foo_bar` -> `Foo Bar`) for an unrecognised system. */
export function systemLabel(system: string): string {
  const known = SYSTEM_LABELS[system];
  if (known !== undefined) {
    return known;
  }
  return system
    .split("_")
    .filter((part) => part.length > 0)
    .map((part) => part[0]!.toUpperCase() + part.slice(1))
    .join(" ");
}

function asNumber(limit: unknown): number | undefined {
  if (typeof limit === "number") {
    return Number.isFinite(limit) ? limit : undefined;
  }
  if (typeof limit === "string") {
    const value = Number.parseFloat(limit);
    return Number.isFinite(value) ? value : undefined;
  }
  return undefined;
}

/** The `note` field, if the row carries one and it is a string -- never rendered otherwise. */
export function noteOf(row: VendorLimitValue): string {
  const note = row?.note;
  return typeof note === "string" ? note : "";
}

function rawFallback(target: string, row: VendorLimitValue): string {
  return `${target}: ${JSON.stringify(row)}`;
}

/** Megabytes at one decimal place, `Number.toFixed` is fine here: this is a display string, not money. */
function megabytes(bytes: number): string {
  return (bytes / 1_048_576).toFixed(1);
}

/**
 * The seven documented targets, verbatim, plus the vendor-specific `*_per_minute`/`*_page_size`/byte-cap
 * variants actually seeded alongside them (§B2, phase 3 v7 §A6). An unrecognised `target`, or a recognised
 * one missing `limit`, falls back to a visible `target: {...}` line rather than a blank cell.
 */
export function formatLimit(target: string, row: VendorLimitValue): string {
  const limit = row?.limit;
  const n = asNumber(limit);

  if (n === undefined) {
    return rawFallback(target, row);
  }

  switch (target) {
    case "per_minute":
      return `${n} requests / minute`;
    case "per_hour":
      return `${n} requests / hour`;
    case "per_day":
      return `${n} requests / day`;
    case "page_size":
      return `${n} records per page`;
    case "concurrency":
      return `${n} concurrent connections`;
    case "attachment_share_of_daily": {
      const pct = Math.round(n * 100);
      return `${pct}% of daily attachment quota`;
    }
    case "body_bytes":
    case "payload_bytes":
      return `${megabytes(n)} MB per request`;

    // Vendor-specific variants seeded alongside the documented targets, not part of the original
    // contract but real rows an operator will otherwise see as raw JSON every day.
    case "app_per_minute":
      return `${n} requests / minute (app-wide)`;
    case "reports_per_minute":
      return `${n} requests / minute (reports)`;
    case "batch_per_minute":
      return `${n} requests / minute (batch)`;
    case "refresh_per_minute":
      return `${n} requests / minute (token refresh)`;
    case "attachment_per_minute":
      return `${n} requests / minute (attachments)`;
    case "attachment_page_size":
      return `${n} records per page (attachments)`;
    case "batch_ops":
      return `${n} operations per batch`;
    case "batch_items":
      return `${n} items per batch`;

    default:
      return rawFallback(target, row);
  }
}
